import { isNumber } from 'lodash';
import { SliderInput } from '@meitu/saas-ui';
import { hexToHsva, hsvaToHexa } from '@meitu/saas-colors/dist/esm/utils/color-convert';
import { type TextLayer } from '@meitu/saas-editor-core/es/layers/text-layer';
import { type IFontGlow, type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { ColorGroup } from '@meitu/xdesign-widgets';
import { EffectPropsContainer } from '@/components/effect-props-container';
import { EffectPropsGroup } from '@/components/effect-props-group';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useLocaleStore } from '@/i18n';
import { useLayerProps } from '../../hooks';
import { type WidgetCommonProps } from '../../types';
import { Tracker } from '../../utils';

type Props = WidgetCommonProps;

const FALLBACK_COLOR = '#000000ff';

export const GlowInspector: React.FC<Props> = ({ layerId, recordId }) => {
    const { $t } = useLocaleStore();

    const editor = useEditor();
    const glow = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const textGlow = layerData.textGlow || [];

        return textGlow.length > 0 ? textGlow[0] : null;
    }) as IFontGlow | null;

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const hidden = glow ? Boolean(glow.hidden) : true;
    const color = glow ? glow.color : FALLBACK_COLOR;
    const hsva = hexToHsva(color);
    const opacity = Math.round(hsva.a * 100);
    const width = glow ? Math.round(glow.width) : 0;

    const add = () => {
        if (glow) return;
        editor.getInstance<TextLayer>('TextLayer').addGlow(layerId);

        Tracker.addGlow({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            panelPosition: 'secondary',
        });
    };

    const remove = () => {
        if (!glow) return;
        editor.getInstance<TextLayer>('TextLayer').removeEffect(layerId, 'textGlow');

        Tracker.removeGlow({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            panelPosition: 'secondary',
        });
    };

    const setVisibility = (flag: boolean) => {
        if (!glow) return;
        editor.getInstance<TextLayer>('TextLayer').setEffectVisibility(layerId, 'textGlow', flag);

        Tracker.toggleGlow({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            panelPosition: 'secondary',
        });
    };

    const handleColorPickerOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openColorPicker({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                kind: 'textGlow',
                panelPosition: 'secondary',
            });
        }
    };

    const adjustColor = (value: string, options = { writeHistory: true }) => {
        if (!glow) return;

        const payload = { idx: 0, data: { color: value } };

        editor.getInstance<TextLayer>('TextLayer').adjustGlowProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustGlowProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'secondary',
            });
        }
    };

    const adjustWidth = (value: number, options = { writeHistory: true }) => {
        if (!glow || !isNumber(value)) return;

        const width = Math.round(value);
        const payload = { idx: 0, data: { width } };
        editor.getInstance<TextLayer>('TextLayer').adjustGlowProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustGlowProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'secondary',
            });
        }
    };

    const adjustOpacity = (value: number, options = { writeHistory: true }) => {
        if (!glow || !isNumber(value)) return;

        const opacity = Math.round(value);
        const hsva = hexToHsva(glow.color);
        const nextColor = hsvaToHexa({ ...hsva, a: opacity / 100 });
        const payload = { idx: 0, data: { color: nextColor } };

        editor.getInstance<TextLayer>('TextLayer').adjustGlowProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustGlowProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'secondary',
            });
        }
    };

    return (
        <EffectPropsContainer
            icon="ContourBold"
            title={$t((v) => v.textStyleAdd.glow)}
            showAdd={glow == null}
            visible={!hidden}
            onAdd={add}
            onRemove={remove}
            onToggleVisible={() => setVisibility(!hidden)}
        >
            {glow ? (
                <div className="text-glow-inspector">
                    <EffectPropsGroup disabled={hidden}>
                        {/* 颜色 */}
                        <LabelAction title={$t((v) => v.textStyleAdd.color)} align>
                            <ColorGroup
                                popupTriggerSize={{ width: 58, height: 26 }}
                                color={color}
                                setColor={(color) => adjustColor(color, { writeHistory: false })}
                                setColorAfter={(color, _, { isBrandColor }) => {
                                    adjustColor(color, { writeHistory: true });

                                    if (isBrandColor) {
                                        window._stat_track?.('hb_layers_func_use', {
                                            func_id: '1_107',
                                        });
                                    }
                                }}
                                onOpenChange={handleColorPickerOpenChange}
                                showIcon={false}
                                popupOverlayClassName="effect-color-picker"
                                placement="left"
                                onToggleBizTab={(bizTab) => {
                                    if (bizTab === 'team') {
                                        window._stat_track?.('hb_layers_func_click', {
                                            func_id: '1_107',
                                        });
                                    }
                                }}
                            />
                        </LabelAction>

                        {/* 范围 */}
                        <LabelAction title={$t((v) => v.textStyleAdd.range)} align>
                            <SliderInput
                                showStepBtn
                                value={width}
                                min={0}
                                max={100}
                                precision={1}
                                type="horizontal"
                                onChange={(value) => adjustWidth(value, { writeHistory: false })}
                                onAfterChange={(value) => adjustWidth(value, { writeHistory: true })}
                            />
                        </LabelAction>

                        {/* 透明度 */}
                        <LabelAction title={$t((v) => v.textStyleAdd.opacity)} align>
                            <SliderInput
                                showStepBtn
                                value={opacity}
                                min={0}
                                max={100}
                                precision={1}
                                type="horizontal"
                                onChange={(value) => adjustOpacity(value, { writeHistory: false })}
                                onAfterChange={(value) => adjustOpacity(value, { writeHistory: true })}
                            />
                        </LabelAction>
                    </EffectPropsGroup>
                </div>
            ) : null}
        </EffectPropsContainer>
    );
};
